import type {
  BossDef,
  GameContent,
  LootKind,
  MissionCheck,
  MissionDef,
  RaidDef,
  RulesetOverrides,
  VocationId,
} from '@estandarte/engine';
import bossesJson from './bosses.json';
import charactersJson from './characters.json';
import chroniclesJson from './chronicles.json';
import guildsJson from './guilds.json';
import lootJson from './loot.json';
import mapJson from './map.json';
import missionsJson from './missions.json';
import vocationsJson from './vocations.json';
import { strings } from './strings.pt-BR';

export { strings };

export interface GuildDef {
  /** Id da guilda no kit (rose, unit…). */
  id: string;
  /** Id de cor usado pelo motor (red, blue…). */
  color: string;
  name: string;
  colorName: string;
  hex: string;
  crest: string;
  world: string | null;
  flavor: string;
}

export interface VocationDef {
  id: string;
  vocation: VocationId;
  name: string;
  role: string;
  /** Resumo curto do poder (destaque da carta). */
  headline: string;
  /** Uma frase de ambientação. */
  flavor: string;
  text: string;
}

export type LabelPos = 't' | 'b' | 'l' | 'r';

export interface ThemeDef {
  id: string;
  content: GameContent;
  viewBox: [number, number];
  regions: Record<string, { name: string; color: string; bonus: number; dieFace: number }>;
  territories: Record<
    string,
    { name: string; x: number; y: number; region: string; symbol: string; temple: boolean; labelPos: LabelPos; description: string }
  >;
  lairs: Record<string, { x: number; y: number; adjacent: string[] }>;
  /** Ligações do mapa (para desenhar estradas e rotas de barco). */
  links: { a: string; b: string; sea: boolean; kind: string; bend: number }[];
  wildCards: Record<string, { name: string }>;
  bosses: Record<string, { name: string; title: string; raidText: string; color: string; sigil: string }>;
  /** Loot por efeito do motor. */
  loot: Record<LootKind, { id: string; name: string; headline: string; text: string; category: string; timing: string }>;
  missions: Record<string, { name: string; text: string }>;
  vocations: Record<VocationId, VocationDef>;
  guilds: GuildDef[];
  strings: typeof strings;
}

const COLOR_NAMES: Record<string, string> = {
  red: 'vermelha',
  blue: 'azul',
  green: 'verde',
  yellow: 'amarela',
  black: 'preta',
  white: 'branca',
};

const guilds: GuildDef[] = guildsJson.map((g) => ({ ...g, colorName: COLOR_NAMES[g.color] ?? g.color }));
const colorOfGuild = (id: string) => guilds.find((g) => g.id === id)?.color ?? id;

type RawCheck = { type: string; [k: string]: unknown };

/** Converte o `check` do kit (guildas por id) para o formato do motor (guildas por cor). */
function toCheck(c: RawCheck): MissionCheck {
  if (c.type === 'eliminate') {
    return {
      type: 'eliminate',
      color: colorOfGuild(String(c.guild)),
      fallback: c.fallback ? toCheck(c.fallback as RawCheck) : undefined,
    };
  }
  return c as unknown as MissionCheck;
}

const regionDie = [...mapJson.regions].sort((a, b) => a.dieFace - b.dieFace).map((r) => r.id);

const content: GameContent = {
  map: {
    regions: mapJson.regions.map((r) => ({ id: r.id, bonus: r.bonus })),
    territories: mapJson.territories.map((t) => ({ id: t.id, region: t.region, symbol: t.symbol, temple: t.temple })),
    links: [
      ...mapJson.landLinks.map((l) => ({ a: l.a, b: l.b, kind: l.kind })),
      ...mapJson.seaRoutes.map((l) => ({ a: l.a, b: l.b, sea: true, kind: 'sea' })),
    ],
    lairs: mapJson.lairs.map((l) => ({ region: l.region, adjacent: l.adjacent })),
    regionDie,
    wildCards: mapJson.wildCards.map((w) => w.id),
  },
  bosses: bossesJson.map(
    (b): BossDef => ({
      id: b.id,
      hp: b.hp,
      defenseDice: b.defenseDice,
      timer: b.rounds,
      lootDraw: b.lootDraw,
      raid: b.raid as RaidDef,
    }),
  ),
  missions: missionsJson.map((m): MissionDef => ({ ...toCheck(m.check as RawCheck), id: m.id })),
  loot: lootJson.map((l) => l.effect as LootKind),
};

export const tibiaTheme: ThemeDef = {
  id: 'tibia',
  content,
  viewBox: mapJson.viewBox as [number, number],
  regions: Object.fromEntries(
    mapJson.regions.map((r) => [r.id, { name: r.name, color: r.color, bonus: r.bonus, dieFace: r.dieFace }]),
  ),
  territories: Object.fromEntries(
    mapJson.territories.map((t) => [
      t.id,
      {
        name: t.name,
        x: t.x,
        y: t.y,
        region: t.region,
        symbol: t.symbol,
        temple: t.temple,
        labelPos: t.labelPos as LabelPos,
        description: t.description,
      },
    ]),
  ),
  lairs: Object.fromEntries(mapJson.lairs.map((l) => [l.region, { x: l.x, y: l.y, adjacent: l.adjacent }])),
  links: [
    ...mapJson.landLinks.map((l) => ({ a: l.a, b: l.b, sea: false, kind: l.kind, bend: 0 })),
    ...mapJson.seaRoutes.map((l) => ({ a: l.a, b: l.b, sea: true, kind: 'sea', bend: l.bend })),
  ],
  wildCards: Object.fromEntries(mapJson.wildCards.map((w) => [w.id, { name: w.name }])),
  bosses: Object.fromEntries(
    bossesJson.map((b) => [b.id, { name: b.name, title: b.title, raidText: b.raidText, color: b.color, sigil: b.sigil }]),
  ),
  loot: Object.fromEntries(
    lootJson.map((l) => [l.effect, { id: l.id, name: l.name, headline: l.headline, text: l.text, category: l.category, timing: l.timing }]),
  ) as ThemeDef['loot'],
  missions: Object.fromEntries(missionsJson.map((m) => [m.id, { name: m.name, text: m.text }])),
  vocations: Object.fromEntries(vocationsJson.map((v) => [v.vocation, v as VocationDef])) as ThemeDef['vocations'],
  guilds,
  strings,
};

export const tibiaContent = content;

/** Texto genérico de um objetivo, para as missões reserva. */
export function checkText(theme: ThemeDef, c: MissionCheck): string {
  switch (c.type) {
    case 'regions': {
      const names = c.regions.map((r) => theme.regions[r]?.name ?? r).join(' e ');
      return `Conquiste ${names} inteiros${c.plusAny ? ` e mais ${c.plusAny} região à sua escolha` : ''}.`;
    }
    case 'territories':
      return c.minTroops
        ? `Controle ${c.count} territórios com pelo menos ${c.minTroops} tropas em cada.`
        : `Controle ${c.count} territórios ao mesmo tempo.`;
    case 'ports':
      return `Controle ${c.count} territórios que tenham rota de barco.`;
    case 'bossKills':
      return `Mate ${c.count} bosses.`;
    case 'eliminate':
      return `Elimine a guilda ${guilds.find((g) => g.color === c.color)?.name ?? c.color}.`;
  }
}

/** Nome e texto de uma missão (inclui as missões reserva). */
export function missionInfo(theme: ThemeDef, m: MissionDef): { name: string; text: string } {
  const base = theme.missions[m.id];
  if (base) return base;
  const orig = m.fallbackOf ? theme.missions[m.fallbackOf] : undefined;
  return {
    name: orig ? `${orig.name} (reserva)` : 'Missão reserva',
    text: checkText(theme, m),
  };
}

export function missionText(theme: ThemeDef, m: MissionDef): string {
  return missionInfo(theme, m).text;
}

export function guildOf(theme: ThemeDef, color: string): GuildDef {
  return theme.guilds.find((g) => g.color === color) ?? theme.guilds[0];
}

export interface CharacterDef {
  name: string;
  /** Apelido curto, em minúsculas ("a escriba das sombras"). */
  epithet: string;
  /** Território de onde vem (id do mapa). */
  origin: string;
  story: string;
  quote: string;
}

export interface ChronicleDef {
  id: string;
  title: string;
  world: string;
  period: string;
  /** Guildas do tema ligadas à história (ids do guilds.json). */
  guilds: string[];
  story: string;
  /** O que a guerra mudou no Tibia. */
  changed: string;
  /** A regra que a era traz para a mesa, em texto. */
  rule: string;
  /** Verso do Velho Bardo que abre a partida. */
  verse: string;
  ruleset: RulesetOverrides;
}

/**
 * Crônicas das guerras: cada uma é uma era que se escolhe na nova partida e liga
 * uma regra do motor. Histórias pesquisadas em relatos da comunidade (ver DECISIONS.md).
 */
export const CHRONICLES: ChronicleDef[] = chroniclesJson as ChronicleDef[];

/** Regras de uma era para `GameConfig.ruleset` (sem era: regras clássicas). */
export function eraRuleset(id: string | null | undefined): RulesetOverrides {
  const c = CHRONICLES.find((x) => x.id === id);
  return c ? { ...c.ruleset, era: c.id } : {};
}

/** Personagens dos bots: nome, apelido, cidade de origem, história e uma frase. */
export const CHARACTERS: Record<string, CharacterDef> = Object.fromEntries(charactersJson.map((c) => [c.name, c]));

/** Nomes inventados para bots (cada um tem um personagem em characters.json). */
export const BOT_NAMES = charactersJson.map((c) => c.name);
